// TelcoTrack — odvození stavu smlouvy (Vygenerovaná … Kompletní … Podepsaná).

import {
  BREMENA_CONTRACT_STATUS_LABELS_CS,
  normalizeBremenaContractStatus,
  type BremenaContractStatus,
} from '../constants/bremena.constants';

export interface DeriveBremenaContractStatusInput {
  /** Explicitní stav na nástroji / dokumentu (nový nebo legacy). */
  instrumentStatus?: string | null;
  /** Dokumentová osa (schvalen_k_podpisu, castecne_podepsan, …). */
  documentStatus?: string | null;
  signedAt?: string | Date | null;
  effectiveAt?: string | Date | null;
  /** True when an E3 PDF/HTML exists for this instrument. */
  hasGeneratedDocument?: boolean | null;
  /**
   * Drive `modifiedTime` of the linked Google Doc is newer than generate time
   * (or an explicit „doc updated“ hook fired).
   */
  googleDocEdited?: boolean | null;
}

const STATUS_RANK: Record<BremenaContractStatus, number> = {
  vygenerovana: 1,
  upravena: 2,
  kompletni: 3,
  odeslana: 4,
  podepsana: 5,
};

/**
 * Preferuje signedAt → Podepsaná, jinak explicitní / legacy status,
 * document axis (odeslání / podpis), Drive edit → Upravená (jen pod Kompletní),
 * Kompletní / existence dokumentu → Vygenerovaná. Bez dokumentu a bez signálu → null.
 */
export function deriveBremenaContractStatus(
  input: DeriveBremenaContractStatusInput,
): BremenaContractStatus | null {
  if (input.signedAt || input.effectiveAt) return 'podepsana';

  const fromExplicit = normalizeBremenaContractStatus(input.instrumentStatus);
  if (fromExplicit === 'podepsana' || fromExplicit === 'odeslana') return fromExplicit;

  const doc = (input.documentStatus ?? '').trim().toLowerCase();
  if (doc === 'podpisy_kompletni' || doc === 'ucinny') return 'podepsana';
  if (doc === 'castecne_podepsan' || doc === 'schvalen_k_podpisu') return 'odeslana';

  // Kompletní stays (Drive edit of a complete doc must not demote).
  if (fromExplicit === 'kompletni') return 'kompletni';

  if (fromExplicit === 'upravena' || input.googleDocEdited) {
    return 'upravena';
  }
  if (fromExplicit === 'vygenerovana' || input.hasGeneratedDocument) {
    return 'vygenerovana';
  }
  return null;
}

export function bremenaContractStatusLabel(status: BremenaContractStatus | string | null | undefined): string {
  const key = normalizeBremenaContractStatus(status);
  if (key) return BREMENA_CONTRACT_STATUS_LABELS_CS[key];
  const raw = (status ?? '').trim();
  return raw || '—';
}

/** Rank for aggregating multiple instruments (highest wins). */
export function bremenaContractStatusRank(status: BremenaContractStatus | string | null | undefined): number {
  const key = normalizeBremenaContractStatus(status);
  return key ? STATUS_RANK[key] : 0;
}

/**
 * Stáhnout PDF / Docs — unlocked once an instrument has a generated document
 * (Vygenerovaná … Podepsaná). Incomplete fill does not block download.
 */
export function bremenaContractAllowsDownload(
  status: BremenaContractStatus | string | null | undefined,
): boolean {
  return bremenaContractStatusRank(status) >= STATUS_RANK.vygenerovana;
}

/**
 * Odeslat / Odesláno poštou — unlocked once fill is complete (Kompletní) or later.
 * Upravená / Vygenerovaná remain locked for send.
 * Callers must also require a successful validation_report (readyForSignature).
 */
export function bremenaContractAllowsSend(
  status: BremenaContractStatus | string | null | undefined,
): boolean {
  return bremenaContractStatusRank(status) >= STATUS_RANK.kompletni;
}

/**
 * Připravit k podpisu — same gate as send on status axis; production still needs
 * validation_report.readyForSignature (no [DOPLNIT], GP, shares, …).
 */
export function bremenaContractAllowsPrepareSignature(
  status: BremenaContractStatus | string | null | undefined,
): boolean {
  const key = normalizeBremenaContractStatus(status);
  // Allow prepare from any generated draft so validation can promote → kompletni.
  return key != null && STATUS_RANK[key] >= STATUS_RANK.vygenerovana && key !== 'podepsana';
}

/**
 * Combined gate: status Kompletní+ and validation_report says ready.
 * Working / DRAFT PDFs must not unlock send.
 */
export function bremenaContractAllowsSendValidated(
  status: BremenaContractStatus | string | null | undefined,
  readyForSignature: boolean | null | undefined,
): boolean {
  return bremenaContractAllowsSend(status) && readyForSignature === true;
}

/**
 * @deprecated Prefer {@link bremenaContractAllowsSend} (or {@link bremenaContractAllowsDownload}).
 * Kept as alias of send gate for older call sites.
 */
export function bremenaContractAllowsDownloadOrSend(
  status: BremenaContractStatus | string | null | undefined,
): boolean {
  return bremenaContractAllowsSend(status);
}

export function maxBremenaContractStatus(
  statuses: ReadonlyArray<BremenaContractStatus | string | null | undefined>,
): BremenaContractStatus | null {
  let best: BremenaContractStatus | null = null;
  let bestRank = 0;
  for (const status of statuses) {
    const key = normalizeBremenaContractStatus(status);
    if (!key) continue;
    const rank = STATUS_RANK[key];
    if (rank > bestRank) {
      best = key;
      bestRank = rank;
    }
  }
  return best;
}

/**
 * Advance only forward (Vygenerovaná → … → Podepsaná). Returns next if allowed.
 */
export function advanceBremenaContractStatus(
  current: string | null | undefined,
  next: BremenaContractStatus,
): BremenaContractStatus {
  const cur = normalizeBremenaContractStatus(current);
  if (!cur) return next;
  return STATUS_RANK[next] >= STATUS_RANK[cur] ? next : cur;
}
